import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Avatar,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import { 
  Send, 
  AutoAwesome, 
  Person,
  Schedule,
  ContactPhone,
  Assessment,
  Description,
  DeleteOutline,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage, postQuery, getDocumentSignedUrl, DocumentSource } from './copilotApi';
import { useAuth } from '../../context/AuthContext';
import { loadChatHistory, saveChatHistory, clearChatHistory } from '../../services/chatHistoryService';
import { Download, Description as DocIcon } from '@mui/icons-material';

// Starter prompt suggestions organized by category
const STARTER_PROMPTS = [
  {
    category: 'Schedule',
    icon: Schedule,
    color: '#48BB78', // green
    prompts: [
      'What scenes are we shooting on Day 1?',
      'What are the 5 most ambitious shooting days?',
      'Show me the production risk summary',
    ],
  },
  {
    category: 'Contacts',
    icon: ContactPhone,
    color: '#ED8936', // orange
    prompts: [
      "What is the 1st AD's phone number?",
      'Who is the Line Producer?',
    ],
  },
  {
    category: 'Analysis',
    icon: Assessment,
    color: '#9F7AEA', // purple
    prompts: [
      'How complex is Day 6?',
      "What's our current page drift?",
    ],
  },
  {
    category: 'Documents',
    icon: Description,
    color: '#4299E1', // blue
    prompts: [
      'What time is crew call on Day 1?',
      'What are the script colors for each episode?',
    ],
  },
];

interface ChatCoPilotProps {
  /** Optional initial prompt to pre-fill (e.g., from "Explore with AI" button) */
  initialPrompt?: string;
  /** Whether to show the starter prompt tiles (default: true) */
  enableStarterPrompts?: boolean;
  /** Pre-computed response to display immediately (no AI call needed) */
  precomputedResponse?: string;
}

export const ChatCoPilot: React.FC<ChatCoPilotProps> = ({ 
  initialPrompt,
  enableStarterPrompts = true,
  precomputedResponse
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [lastProcessedPrompt, setLastProcessedPrompt] = useState<string>('');
  const scrollContainerRef = useRef<null | HTMLDivElement>(null);

  // Check if we should show starter prompts (only welcome message exists AND enabled via prop)
  const showStarterPrompts = enableStarterPrompts && messages.length === 1 && messages[0]?.id === 'ai-welcome';

  // Welcome message constant
  const welcomeMessage: ChatMessage = {
    id: 'ai-welcome',
    sender: 'ai',
    text: 'I am the Production GPT. Ask me about schedules, crew contacts, production analysis, or search documents.',
  };

  // Load chat history from Firestore on mount
  useEffect(() => {
    const loadHistory = async () => {
      if (user?.uid) {
        setIsLoadingHistory(true);
        try {
          const history = await loadChatHistory(user.uid);
          if (history.length > 0) {
            setMessages(history);
          } else {
            setMessages([welcomeMessage]);
          }
        } catch (error) {
          console.error('Failed to load chat history:', error);
          setMessages([welcomeMessage]);
        } finally {
          setIsLoadingHistory(false);
        }
      } else {
        setMessages([welcomeMessage]);
        setIsLoadingHistory(false);
      }
    };
    
    loadHistory();
  }, [user?.uid]);

  // Save chat history to Firestore when messages change
  useEffect(() => {
    if (user?.uid && user?.email && messages.length > 0 && !isLoadingHistory) {
      // Don't save if only welcome message
      if (messages.length === 1 && messages[0]?.id === 'ai-welcome') {
        return;
      }
      saveChatHistory(user.uid, user.email, messages);
    }
  }, [messages, user?.uid, user?.email, isLoadingHistory]);

  // Clear chat history handler
  const handleClearHistory = useCallback(async () => {
    if (user?.uid) {
      await clearChatHistory(user.uid);
      setMessages([welcomeMessage]);
    }
  }, [user?.uid]);

  // Track which documents are being downloaded
  const [downloadingDocs, setDownloadingDocs] = useState<Set<string>>(new Set());

  // Handle document download
  const handleDocumentDownload = useCallback(async (gcsUri: string, title: string) => {
    setDownloadingDocs(prev => new Set(prev).add(gcsUri));
    
    try {
      const result = await getDocumentSignedUrl(gcsUri);
      
      if (result.success && result.signedUrl) {
        // Open in new tab for viewing/download
        window.open(result.signedUrl, '_blank');
      } else {
        console.error('Failed to get download URL:', result.error);
        alert('Failed to generate download link. Please try again.');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download document. Please try again.');
    } finally {
      setDownloadingDocs(prev => {
        const next = new Set(prev);
        next.delete(gcsUri);
        return next;
      });
    }
  }, []);

  // Handle initial prompt from Insights page "Explore with AI" buttons
  useEffect(() => {
    if (initialPrompt && initialPrompt.trim() && initialPrompt !== lastProcessedPrompt) {
      setCurrentInput(initialPrompt);
      setLastProcessedPrompt(initialPrompt);
    }
  }, [initialPrompt, lastProcessedPrompt]);

  // Handle precomputed response from Insights page (show immediately without AI call)
  const [lastPrecomputedResponse, setLastPrecomputedResponse] = useState<string>('');
  useEffect(() => {
    if (precomputedResponse && precomputedResponse.trim() && precomputedResponse !== lastPrecomputedResponse) {
      // Add the precomputed explanation directly as an AI message
      const aiResponse: ChatMessage = {
        id: `ai-precomputed-${Date.now()}`,
        sender: 'ai',
        text: precomputedResponse,
      };
      setMessages((prev) => [...prev, aiResponse]);
      setLastPrecomputedResponse(precomputedResponse);
    }
  }, [precomputedResponse, lastPrecomputedResponse]);

  // Handle clicking a starter prompt
  const handlePromptClick = (prompt: string) => {
    setCurrentInput(prompt);
  };

  // Scroll to bottom when new messages are added - using manual scroll to prevent page jump
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentInput.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: currentInput,
    };

    setMessages((prev) => [...prev, userMessage]);
    setCurrentInput('');
    setIsLoading(true);

    try {
      const apiResponse = await postQuery(userMessage.text, messages);
      if (apiResponse.success) {
        setMessages((prev) => [...prev, apiResponse.response]);
      } else {
        const errorResponse: ChatMessage = {
          id: `ai-error-${Date.now()}`,
          sender: 'ai',
          text: `Error: ${apiResponse.error || 'Failed to get response.'}`,
        };
        setMessages((prev) => [...prev, errorResponse]);
      }
    } catch (err) {
      const errorResponse: ChatMessage = {
        id: `ai-error-${Date.now()}`,
        sender: 'ai',
        text: 'A fatal error occurred.',
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1A202C',
        borderLeft: '1px solid #4A5568',
        borderRadius: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #4A5568',
          backgroundColor: '#2D3748',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AutoAwesome sx={{ mr: 1.5, color: '#4299E1' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
            Production GPT
          </Typography>
        </Box>
        <Tooltip title="Clear chat history">
          <IconButton
            onClick={handleClearHistory}
            size="small"
            sx={{
              color: '#9CA3AF',
              '&:hover': {
                color: '#F56565',
                backgroundColor: 'rgba(245, 101, 101, 0.1)',
              },
            }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Message History */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          // Custom scrollbar styles
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#1A202C',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#4A5568',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#718096',
          },
        }}
      >
        {isLoadingHistory && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mr: 2 }} />
            <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
              Loading chat history...
            </Typography>
          </Box>
        )}

        {!isLoadingHistory && messages.map((msg) => (
          <Box
            key={msg.id}
            sx={{
              display: 'flex',
              mb: 2,
              flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row',
            }}
          >
            <Avatar
              sx={{
                bgcolor: msg.sender === 'ai' ? '#4299E1' : '#4A5568',
                width: 32,
                height: 32,
                mr: msg.sender === 'user' ? 0 : 1.5,
                ml: msg.sender === 'user' ? 1.5 : 0,
              }}
            >
              {msg.sender === 'ai' ? (
                <AutoAwesome sx={{ fontSize: 18 }} />
              ) : (
                <Person sx={{ fontSize: 18 }} />
              )}
            </Avatar>
            <Paper
              sx={{
                p: 1.5,
                maxWidth: '85%',
                backgroundColor: msg.sender === 'ai' ? '#2D3748' : '#4299E1',
                borderRadius: '12px',
                color: 'white',
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  my: 1,
                  fontSize: '0.85rem',
                },
                '& th, & td': {
                  border: '1px solid #4A5568',
                  padding: '8px',
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: '#1A202C',
                },
                '& p': {
                  margin: '0 0 8px 0',
                },
                '& p:last-child': {
                  margin: 0,
                }
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {msg.text}
              </ReactMarkdown>
              
              {/* Document Sources from RAG */}
              {msg.sender === 'ai' && msg.documentSources && msg.documentSources.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 1.5,
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <DocIcon sx={{ fontSize: 14 }} />
                    Source Documents
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {msg.documentSources.map((doc, idx) => (
                      <Chip
                        key={`${msg.id}-doc-${idx}`}
                        label={doc.title || 'Document'}
                        size="small"
                        icon={<Download sx={{ fontSize: '14px !important' }} />}
                        onClick={() => handleDocumentDownload(doc.gcs_uri, doc.title)}
                        disabled={downloadingDocs.has(doc.gcs_uri)}
                        sx={{
                          backgroundColor: 'rgba(66, 153, 225, 0.15)',
                          color: '#63B3ED',
                          border: '1px solid rgba(66, 153, 225, 0.3)',
                          fontSize: '0.75rem',
                          height: 'auto',
                          py: 0.5,
                          '& .MuiChip-icon': {
                            color: '#63B3ED',
                          },
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            lineHeight: 1.3,
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(66, 153, 225, 0.25)',
                            borderColor: '#4299E1',
                          },
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        ))}

        {/* Starter Prompt Chips - shown only in initial state */}
        {showStarterPrompts && !isLoading && (
          <Box sx={{ mt: 2 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#9CA3AF', 
                display: 'block', 
                mb: 1.5,
                ml: 0.5,
              }}
            >
              Try asking:
            </Typography>
            {STARTER_PROMPTS.map((category) => (
              <Box key={category.category} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 0.5 }}>
                  <category.icon sx={{ fontSize: 16, color: category.color, mr: 0.5 }} />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: category.color, 
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {category.category}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {category.prompts.map((prompt) => (
                    <Chip
                      key={prompt}
                      label={prompt}
                      onClick={() => handlePromptClick(prompt)}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        color: '#E2E8F0',
                        border: `1px solid ${category.color}40`,
                        fontSize: '0.75rem',
                        height: 'auto',
                        py: 0.5,
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          lineHeight: 1.3,
                        },
                        '&:hover': {
                          backgroundColor: `${category.color}20`,
                          borderColor: category.color,
                        },
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      {/* Input Area */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          p: 2,
          borderTop: '1px solid #4A5568',
          backgroundColor: '#2D3748',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Ask anything about your production..."
          value={currentInput}
          onChange={(e) => setCurrentInput(e.target.value)}
          disabled={isLoading}
          autoComplete="off"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '24px',
              backgroundColor: '#1A202C',
              color: 'white',
              '& fieldset': {
                borderColor: '#4A5568',
              },
              '&:hover fieldset': {
                borderColor: '#4299E1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#4299E1',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#9CA3AF',
              opacity: 1,
            },
          }}
        />
        <IconButton
          type="submit"
          disabled={isLoading || !currentInput.trim()}
          sx={{
            color: '#4299E1',
            '&:disabled': {
              color: '#4A5568',
            },
            '&:hover:not(:disabled)': {
              backgroundColor: 'rgba(66, 153, 225, 0.1)',
            },
          }}
        >
          <Send />
        </IconButton>
      </Box>
    </Paper>
  );
};
